import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import ContactForm from '../components/ContactForm';
import { 
  FaShieldAlt,
  FaLock,
  FaUserShield,
  FaNetworkWired,
  FaCode,
  FaServer,
  FaEye,
  FaCloud,
  FaGlobe
} from 'react-icons/fa';
import bgImage from '../assets/mobapp.jpg';


const CyberSecurityPage = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const securityServices = [
    {
      icon: <FaShieldAlt />,
      title: 'Threat Protection',
      description: 'Comprehensive defense against malware, ransomware, and zero-day attacks',
      color: 'linear-gradient(135deg, #3b82f6, #0ea5e9)'
    },
    {
      icon: <FaLock />,
      title: 'Data Encryption',
      description: 'End-to-end encryption solutions for data at rest and in transit',
      color: 'linear-gradient(135deg, #000000, #8e8e93)'
    },
    {
      icon: <FaUserShield />,
      title: 'Identity Management',
      description: 'Multi-factor authentication and privileged access management',
      color: 'linear-gradient(135deg, #3ddc84, #2bb673)'
    },
    {
      icon: <FaNetworkWired />,
      title: 'Network Security',
      description: 'Next-gen firewalls, intrusion detection, and secure network architecture',
      color: 'linear-gradient(135deg, #61dafb, #00b4d8)'
    },
    {
      icon: <FaCode />,
      title: 'Secure Development',
      description: 'DevSecOps integration and application security testing',
      color: 'linear-gradient(135deg, #8b5cf6, #d946ef)'
    },
    {
      icon: <FaServer />,
      title: 'Cloud Security',
      description: 'AWS/Azure/GCP security hardening and configuration management',
      color: 'linear-gradient(135deg,rgb(0, 222, 247),rgb(154, 0, 237))'
    },
    {
      icon: <FaEye />,
      title: 'Security Monitoring',
      description: '24/7 SIEM solutions with threat intelligence feeds',
      color: 'linear-gradient(135deg,rgb(0, 140, 205),rgb(75, 58, 127))'
    },
    {
      icon: <FaGlobe />,
      title: 'Compliance',
      description: 'GDPR, HIPAA, PCI-DSS, and SOC 2 compliance frameworks',
      color: 'linear-gradient(135deg, #10b981, #14b8a6)'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }
  };

  return (
    <div 
      style={{
        minHeight: '100vh',
        padding: '80px 20px',
        color: '#fff',
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        backgroundAttachment: 'fixed',
        backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.92), rgba(2, 6, 23, 0.95)), url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        textAlign: 'center'
      }}
      ref={ref}
    >
      <motion.div
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={containerVariants}
        style={{ maxWidth: '1280px', margin: '0 auto' }}
      >
        <motion.h1 
          style={{
                fontSize: 'clamp(2.25rem, 3vw + 1rem, 3.5rem)',
            fontWeight: '700',
            marginBottom: '24px',
            background: 'linear-gradient(to right, #8b5cf6, #0ea5e9)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
          variants={itemVariants}
        >
          Cybersecurity Services
        </motion.h1>

        <motion.p 
          style={{
            fontSize: '1.25rem',
            maxWidth: '800px',
            margin: '0 auto 60px',
            color: '#d1d5db',
            lineHeight: '1.7'
          }}
          variants={itemVariants}
        >
          Protect your digital assets with <span style={{ fontWeight: '600', color: '#fff' }}>enterprise-grade security solutions</span> tailored to your organization's risk profile.
        </motion.p>

        <motion.div 
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '30px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}
          variants={containerVariants}
        >
          {securityServices.map((service, index) => (
            <motion.div
              key={index}
              style={{
                background: service.color,
                borderRadius: '16px',
                padding: '30px',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
                color: '#fff',
                textAlign: 'left',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
              variants={itemVariants}
              whileHover={{ 
                y: -8,
                scale: 1.03,
                boxShadow: '0 15px 30px rgba(0, 0, 0, 0.4)'
              }}
            >
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                marginBottom: '20px',
                background: 'rgba(255, 255, 255, 0.15)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
              }}>
                {service.icon}
              </div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                marginBottom: '15px'
              }}>{service.title}</h3>
              <p style={{
                fontSize: '1rem',
                color: '#e5e7eb',
                lineHeight: '1.6'
              }}>{service.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* NovaCrest Cybersecurity Section */}
        <motion.div 
          style={{
            marginTop: '80px',
            background: 'linear-gradient(to right, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9))',
            borderRadius: '24px',
            padding: '50px',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
             maxWidth: 'min(1000px, 90vw)',
               width: '100%',
            margin: '80px auto 0'
          }}
          variants={itemVariants}
          whileHover={{ scale: 1.01 }}
        >
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            marginBottom: '24px',
            background: 'linear-gradient(to right, #8b5cf6, #0ea5e9)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center'
          }}>
            NovaCrest Security Expertise
          </h2>
          
          <p style={{
            fontSize: '1.2rem',
            color: '#d1d5db',
            maxWidth: '800px',
            margin: '0 auto 40px',
            lineHeight: '1.7',
            textAlign: 'center'
          }}>
            Comprehensive protection from perimeter to endpoint and beyond.
          </p>
          
          <ul style={{
            display: 'grid',
           gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
            gap: '20px',
            maxWidth: '900px',
            margin: '0 auto',
            padding: '0',
            listStyle: 'none'
          }}>
            {[
              "Certified ethical hackers on staff",
              "24/7 security operations center",
              "Incident response team with <1hr SLA",
              "Dark web monitoring services",
              "Security awareness training programs",
              "Penetration testing and red teaming",
              "Regulatory compliance specialists",
              "Custom security policy development",
              "Vulnerability assessment programs",
              "Third-party vendor risk management"
            ].map((item, index) => (
              <motion.li
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '16px',
                  background: 'rgba(30, 41, 59, 0.5)',
                  borderRadius: '12px',
                  borderLeft: '4px solid #8b5cf6',
                  transition: 'all 0.3s ease'
                }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ 
                  opacity: 1, 
                  x: 0,
                  transition: { delay: 0.5 + index * 0.1 }
                }}
                whileHover={{ x: 5 }}
              >
                <span style={{
                  display: 'inline-block',
                  marginRight: '12px',
                  color: '#8b5cf6',
                  fontSize: '1.5rem'
                }}>•</span>
                <span style={{
                  color: '#e5e7eb',
                  lineHeight: '1.6'
                }}>{item}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>
        <ContactForm />
      </motion.div>
    </div>
  );
};

export default CyberSecurityPage;